import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Crown, Zap, Building2, Star, Check, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';

const SubscriptionStatus = ({ className = "" }) => {
  const navigate = useNavigate();
  const { 
    subscription, 
    features, 
    subscriptionTiers, 
    getCurrentTier, 
    upgradeSubscription,
    loading 
  } = useSubscription();
  
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');

  if (loading) {
    return (
      <div className={`bg-slate-800/50 rounded-lg p-3 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-slate-600 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-slate-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  const currentTier = getCurrentTier();
  
  const getTierIcon = (tierName) => {
    switch (tierName) {
      case 'starter': return <Star className="h-4 w-4" />;
      case 'professional': return <Zap className="h-4 w-4" />;
      case 'business': return <Building2 className="h-4 w-4" />;
      case 'enterprise': return <Crown className="h-4 w-4" />;
      default: return <Star className="h-4 w-4" />;
    }
  };

  const getTierColor = (tierName) => {
    switch (tierName) {
      case 'starter': return 'text-slate-400';
      case 'professional': return 'text-blue-400';
      case 'business': return 'text-green-400';
      case 'enterprise': return 'text-purple-400';
      default: return 'text-slate-400';
    }
  };

  const handleUpgrade = async (tier) => {
    const success = await upgradeSubscription(tier.name, billingCycle);
    if (success) {
      setShowUpgradeDialog(false);
      toast.success(`Successfully upgraded to ${tier.display_name}! 🎉`);
    }
  };

  const formatPrice = (monthly, annual, cycle) => {
    if (monthly === 0) return 'Free';
    if (cycle === 'annual') return `$${annual}/year`;
    return `$${monthly}/month`;
  };

  // Check if user is admin
  const isAdmin = subscription?.is_admin || subscription?.tier === 'admin';
  
  // Check if user is on trial
  const isTrial = subscription?.is_trial;
  const trialEnds = subscription?.trial_ends;
  
  // Calculate days remaining if on trial
  let daysRemaining = null;
  if (isTrial && trialEnds) {
    const trialEnd = new Date(trialEnds);
    const now = new Date();
    const diffTime = trialEnd - now;
    daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (daysRemaining < 0) daysRemaining = 0;
  }

  return (
    <>
      <div className={`bg-slate-800/50 rounded-lg p-3 ${className}`}>
        {/* Trial Warning Banner */}
        {isTrial && daysRemaining !== null && daysRemaining <= 7 && (
          <div className={`mb-2 p-2 rounded text-xs font-medium ${
            daysRemaining <= 3 ? 'bg-red-500/20 text-red-200 border border-red-500/50' : 
            'bg-yellow-500/20 text-yellow-200 border border-yellow-500/50'
          }`}>
            ⚠️ Trial ends in {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}!
          </div>
        )}
        
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div className={isAdmin ? 'text-yellow-400' : getTierColor(currentTier?.name || 'starter')}>
              {isAdmin ? <Crown className="h-4 w-4" /> : getTierIcon(currentTier?.name || 'starter')}
            </div>
            <span className="text-white text-sm font-medium">
              {isAdmin ? 'Admin (Unrestricted)' : 
               (currentTier?.display_name || subscription?.tier_display || subscription?.tier_name || 'Starter Plan')}
            </span>
            {isTrial && (
              <Badge variant="outline" className="text-green-400 border-green-400 bg-green-400/10 text-xs">
                Trial
              </Badge>
            )}
          </div>
          {isAdmin ? (
            <Badge variant="outline" className="text-yellow-400 border-yellow-400 bg-yellow-400/10">
              <Crown className="h-3 w-3 mr-1" />
              ADMIN
            </Badge>
          ) : (
            <Badge 
              variant="outline" 
              className={`${getTierColor(currentTier?.name || subscription?.tier || 'starter')} border-current`}
            >
              {((currentTier?.name || subscription?.tier || 'starter').toUpperCase())}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center justify-between">
          <div className="text-xs text-slate-400">
            {isAdmin ? (
              <span className="text-yellow-300">Unlimited Access to All Features</span>
            ) : isTrial && daysRemaining !== null ? (
              <span className={daysRemaining <= 3 ? 'text-red-300' : 'text-green-300'}>
                {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining in trial
              </span>
            ) : (
              features && (
                <span>
                  {features.limits.business_plans === -1 ? 'Unlimited' : features.limits.business_plans} Business Plans
                  {features.limits.subsidiaries !== undefined && 
                    ` • ${features.limits.subsidiaries === -1 ? 'Unlimited' : features.limits.subsidiaries} Subsidiaries`
                  }
                </span>
              )
            )}
          </div>
          
          {!isAdmin && currentTier?.name !== 'enterprise' && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-blue-300 hover:text-blue-200 text-xs h-6 px-2"
              onClick={() => navigate('/pricing')}
            >
              <ArrowUp className="h-3 w-3 mr-1" />
              Upgrade
            </Button>
          )}
        </div>
      </div>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Choose Your Plan</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Billing Toggle */}
            <div className="flex items-center justify-center space-x-4">
              <span className={`text-sm ${billingCycle === 'monthly' ? 'text-slate-900' : 'text-slate-500'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  billingCycle === 'annual' ? 'bg-blue-600' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'annual' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm ${billingCycle === 'annual' ? 'text-slate-900' : 'text-slate-500'}`}>
                Annual 
                <span className="ml-1 text-green-600 font-medium">(Save 2 months!)</span>
              </span>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {subscriptionTiers.map((tier) => {
                const isCurrentTier = currentTier?.id === tier.id;
                const price = billingCycle === 'annual' ? tier.price_annual : tier.price_monthly;
                
                return (
                  <Card 
                    key={tier.id} 
                    className={`relative cursor-pointer transition-all hover:shadow-lg ${
                      isCurrentTier ? 'ring-2 ring-blue-500 bg-blue-50' : 
                      selectedTier?.id === tier.id ? 'ring-2 ring-green-500' : ''
                    }`}
                    onClick={() => setSelectedTier(tier)}
                  >
                    {tier.name === 'professional' && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-blue-600 text-white">Most Popular</Badge>
                      </div>
                    )}
                    
                    <CardHeader className="text-center">
                      <div className="flex items-center justify-center mb-2">
                        <div className={`p-2 rounded-full ${
                          tier.name === 'starter' ? 'bg-slate-100' :
                          tier.name === 'professional' ? 'bg-blue-100' :
                          tier.name === 'business' ? 'bg-green-100' :
                          'bg-purple-100'
                        }`}>
                          {getTierIcon(tier.name)}
                        </div>
                      </div>
                      <CardTitle className="text-lg">{tier.display_name}</CardTitle>
                      <div className="text-3xl font-bold">
                        {price === 0 ? 'Free' : `$${price}`}
                        {price > 0 && (
                          <span className="text-sm font-normal text-slate-600">
                            /{billingCycle === 'annual' ? 'year' : 'month'}
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <p className="text-sm text-slate-600 mb-4">{tier.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm">
                          <Check className="h-4 w-4 text-green-500 mr-2" />
                          <span>
                            {tier.limits.business_plans === -1 ? 'Unlimited' : tier.limits.business_plans} Business Plans
                          </span>
                        </div>
                        
                        {tier.limits.subsidiaries !== undefined && (
                          <div className="flex items-center text-sm">
                            <Check className="h-4 w-4 text-green-500 mr-2" />
                            <span>
                              {tier.limits.subsidiaries === -1 ? 'Unlimited' : tier.limits.subsidiaries} Subsidiaries
                            </span>
                          </div>
                        )}
                        
                        <div className="flex items-center text-sm">  
                          <Check className="h-4 w-4 text-green-500 mr-2" />
                          <span>
                            {tier.limits.users === -1 ? 'Unlimited' : tier.limits.users} Users
                          </span>
                        </div>

                        {tier.features.includes('performance_tracking') && (
                          <div className="flex items-center text-sm">
                            <Check className="h-4 w-4 text-green-500 mr-2" />
                            <span>Performance Tracking</span>
                          </div>
                        )}

                        {tier.features.includes('business_intelligence') && (
                          <div className="flex items-center text-sm">
                            <Check className="h-4 w-4 text-green-500 mr-2" />
                            <span>Business Intelligence</span>
                          </div>
                        )}

                        {tier.features.includes('api_access') && (
                          <div className="flex items-center text-sm">
                            <Check className="h-4 w-4 text-green-500 mr-2" />
                            <span>API Access</span>
                          </div>
                        )}
                      </div>

                      {isCurrentTier ? (
                        <Button disabled className="w-full">
                          Current Plan
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleUpgrade(tier)}
                          className="w-full"
                          variant={tier.name === 'professional' ? 'default' : 'outline'}
                        >
                          {tier.price_monthly === 0 ? 'Downgrade' : 'Upgrade'}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SubscriptionStatus;